import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { activateIntegrationRequest, deactivateIntegrationRequest, updateTikTokSettingsRequest} from '../../redux/slices/integrationsSlice';
import { fetchFacebookPagesRequest, setSelectedPage, updatePageRequest } from '../../redux/slices/facebookPagesSlice';
import { fetchCustomersRequest, addCustomerRequest, uploadCustomersRequest } from '../../redux/slices/customersSlice';
import { useFacebookAuth } from '../../hooks/useFacebookAuth';
import { useTikTokAuth } from '../../hooks/useTikTokAuth';
import { useInstagramAuth } from '../../hooks/useInstagramAuth';
import type { RootState } from '../../redux/store';
import type { Integration } from './types';

import IntegrationHeader from './detail/IntegrationHeader';
import FacebookProfileView from './detail/FacebookProfileView';
import TikTokSettingsPanel, { type TikTokSettingsValues } from './detail/TikTokSettingsPanel';
import EmailProfileView from './detail/EmailProfileView';

import CustomerListPanel from './detail/CustomerListPanel';
import type { CustomerFormValues } from './detail/AddCustomerForm';
import InstagramProfileView from './detail/InstagramProfileView';
import FacebookPageSelector from './detail/FacebookPageSelector';
import ConnectSection from './detail/ConnectSection';
import SmsProfileView from './detail/smsProfileView';

interface IntegrationDetailProps {
  integration: Integration;
  onBack: () => void;
}

const IntegrationDetail: React.FC<IntegrationDetailProps> = ({ integration, onBack }) => {
  const dispatch = useDispatch();
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);

  const {
    data: apiIntegrations,
    activating,
    loading,
    updatingSettings,
  } = useSelector((state: RootState) => state.integrations);

  const {
    pages: facebookPages,
    loading: pagesLoading,
    selectedPageId,
  } = useSelector((state: RootState) => state.facebookPages);

  const { data: customers, loading: customersLoading } = useSelector(
    (state: RootState) => state.customers
  );

  const { startFacebookAuth } = useFacebookAuth();
  const { startTikTokAuth } = useTikTokAuth();
  const { startInstagramFacebookAuth, startInstagramDirectAuth } = useInstagramAuth();

  const [selectedPageForUpdate, setSelectedPageForUpdate] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [emailFromName, setEmailFromName] = useState<string>('');
  const [showAddCustomerForm, setShowAddCustomerForm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const integrationType = integration.type.toLowerCase();

  const apiIntegration = useMemo(() => {
    return apiIntegrations.find((item) => {
      const provider = item.provider?.toLowerCase();

      if (integrationType === 'instagram') {
        return (provider === 'instagram' || provider === 'instagram_direct');
      }

      return provider === integrationType;
    });
  }, [apiIntegrations, integrationType]);

  const isConnected = Boolean(apiIntegration);
  const isActivating = activating === integration.type;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {document.removeEventListener('mousedown', handleClickOutside);};
  }, []);

  // TikTok settings
  const tkSettings = apiIntegration?.configuration?.settings;
  const privacyLevelMap: Record<string, string> = {
    PUBLIC_TO_EVERYONE: 'Public',
    MUTUAL_FOLLOW_FRIENDS: 'Friends',
    SELF_ONLY: 'Private',
  };

  const tikTokInitialValues: TikTokSettingsValues =
    integrationType === 'tiktok' && tkSettings
      ? {
          visibility: privacyLevelMap[tkSettings.privacyLevel] || 'Public',
          allowComment: !!tkSettings.disable_comment,
          allowDuet: !!tkSettings.disable_duet,
          allowStitch: !!tkSettings.disable_stitch,
          discloseContent: !!tkSettings.brand_content_toggle || !!tkSettings.brand_organic_toggle,
          brandType: tkSettings.brand_content_toggle ? 'branded-content' : 'your-brand',
        }
      : {
          visibility: 'Public',
          allowComment: true,
          allowDuet: true,
          allowStitch: true,
          discloseContent: true,
          brandType: 'your-brand',
        };

  useEffect(() => {
    if (!isConnected ||!apiIntegration?.id ||(integrationType !== 'facebook' && integrationType !== 'instagram')) {
      return;
    }

    const pages = apiIntegration.configuration?.pages || [];
    const selectedPageIdInConfig = apiIntegration.configuration?.selectedPageId;

    if (pages.length > 0) {
      const firstPageId = selectedPageIdInConfig || pages[0].id;
      if (selectedPageId !== firstPageId) {
        dispatch(setSelectedPage(firstPageId));
      }
      return;
    }
    const provider = apiIntegration.provider?.toLowerCase();
    if (integrationType === 'facebook' || (integrationType === 'instagram' && provider === 'instagram')) {
      dispatch(fetchFacebookPagesRequest({integrationId: apiIntegration.id}));
    }
  }, [isConnected,integrationType,apiIntegration?.id,apiIntegration?.provider,selectedPageId,dispatch,]);

  useEffect(() => {
    if (facebookPages.length > 0 && !selectedPageForUpdate) {
      setSelectedPageForUpdate(facebookPages[0].id);
    }
  }, [facebookPages.length, selectedPageForUpdate]);

  useEffect(() => {
    if (isConnected && (integrationType === 'email' || integrationType === 'sms') && restaurantId) {
      dispatch(fetchCustomersRequest({ restaurantId }));
    }
  }, [isConnected, integrationType, restaurantId, dispatch]);

  const handleToggle = async () => {
    if (!restaurantId) {return;}

    if (integrationType === 'facebook') {
      try {
        await startFacebookAuth();
      } catch (error) {
        console.error('Facebook authentication failed:', error);
      }
      return;
    }

    if (integrationType === 'tiktok') {
      try {
        await startTikTokAuth();
      } catch (error) {
        console.error('TikTok authentication failed:', error);
      }
      return;
    }

    if (integrationType === 'email') {
      dispatch(activateIntegrationRequest({
        platform: integration.type,
        restaurantId,
        fromName: emailFromName,
      }));
      return;
    }

    if (integrationType === 'sms') {
      dispatch(activateIntegrationRequest({
        platform: integration.type,
        restaurantId
      }));

      return;
    }

    dispatch(activateIntegrationRequest({
      platform: integration.type,
      restaurantId,
    }));
  };

  const handleInstagramFacebookConnect = async () => {
    try {
      await startInstagramFacebookAuth();
    } catch (error) {
      console.error('Instagram via Facebook authentication failed:', error);
    }
  };

  const handleInstagramDirectConnect = async () => {
    try {
      await startInstagramDirectAuth();
    } catch (error) {
      console.error('Instagram direct authentication failed:', error);
    }
  };

  const pagesFromConfig = apiIntegration?.configuration?.pages || [];
  const availablePages = facebookPages.length > 0 ? facebookPages : pagesFromConfig;
  const selectedPage = isConnected && selectedPageId && availablePages.length > 0 ? availablePages.find((page: any) => page.id === selectedPageId) : null;
 
  const hasPagesConfigured = pagesFromConfig.length > 0;

  const handlePageSelect = (pageId: string) => {
    if (!apiIntegration?.id) {return;}
    dispatch(updatePageRequest({integrationId: apiIntegration.id,pageId,platform: integrationType,}));

    setIsDropdownOpen(false);
  };

  const handleSelectPageClick = () => {
    if (!apiIntegration?.id || !selectedPageForUpdate) {return;}
    dispatch(updatePageRequest({integrationId: apiIntegration.id,pageId: selectedPageForUpdate, platform: integrationType,}));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && restaurantId) {
      const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',];
      const isValidFile = validTypes.includes(file.type) || file.name.endsWith('.csv') || file.name.endsWith('.xlsx');
      
      if (!isValidFile) {
        alert('Please upload a CSV or XLSX file');
        return;
      }

      dispatch(uploadCustomersRequest({ restaurantId, file }));
    }

    event.target.value = '';
  };

  const handleSaveTikTokSettings = (values: TikTokSettingsValues) => {
    if (!restaurantId || !apiIntegration?.id || !values.visibility) {
      return;
    }

    const privacyLevelReverseMap: Record<string, string> = {
      Public: 'PUBLIC_TO_EVERYONE',
      Friends: 'MUTUAL_FOLLOW_FRIENDS',
      Private: 'SELF_ONLY',
    };

    dispatch(updateTikTokSettingsRequest({
      integrationId: apiIntegration.id,
      restaurantId,
      settings: {
        privacyLevel: privacyLevelReverseMap[values.visibility] || 'PUBLIC_TO_EVERYONE',
        disable_comment: values.allowComment,
        disable_duet: values.allowDuet,
        disable_stitch: values.allowStitch,
        brand_content_toggle: values.discloseContent && values.brandType === 'branded-content',
        brand_organic_toggle: values.discloseContent && values.brandType === 'your-brand',
      },
    }));
  };

  const handleSaveCustomer = (values: CustomerFormValues) => {
    if (!restaurantId || !values.name.trim() || !values.email.trim()) {return;}

    const customerData = {
      name: values.name,
      email: values.email,
      phone: values.phone || '',
      address: {
        address: values.address || '',
        city: values.city || '',
        state: values.state || '',
        country: values.country || '',
        zipcode: values.zipcode ? parseInt(values.zipcode) : 0,
      },
      type: values.type || '',
    };

    dispatch(addCustomerRequest({ restaurantId, customerData }));
    setShowAddCustomerForm(false);
  };

  const handleLogout = () => {
    if (!restaurantId || !apiIntegration?.id) {
      return;
    }

    const provider = apiIntegration.provider?.toLowerCase();
    const platform = provider === 'instagram_direct'
        ? 'instagram-direct'
        : integration.type;

    dispatch(deactivateIntegrationRequest({ restaurantId, integrationId: apiIntegration.id, platform}));};

  const tiktokUserInfo = apiIntegration?.configuration?.userInfo || {};

  const isTikTokConnected = isConnected && integrationType === 'tiktok' && (apiIntegration?.configuration?.userInfo || apiIntegration?.configuration?.displayName);
  const tkDisplayName = tiktokUserInfo.display_name || apiIntegration?.configuration?.displayName;
  const tkUsername = tiktokUserInfo.username || apiIntegration?.configuration?.username;
  const tkAvatarUrl = tiktokUserInfo.avatar_large_url || tiktokUserInfo.avatar_url || apiIntegration?.configuration?.avatarUrl;
  const tkIsVerified = tiktokUserInfo.is_verified ?? apiIntegration?.configuration?.isVerified;

  return (
    <div className="min-h-screen flex flex-col">
      <IntegrationHeader
        integration={integration}
        isConnected={isConnected}
        onBack={onBack}
        onLogout={handleLogout}
        disabled={isActivating || loading}
      />

      <div className="flex-1 flex justify-center mt-8">
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full max-w-6xl mx-auto"
        >
          {isConnected && pagesLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mb-4" />
              <p className="text-gray-400 text-sm">Fetching page details...</p>
            </div>
          ) : isConnected && selectedPage && hasPagesConfigured && (integrationType === 'facebook' || integrationType === 'instagram') ? (
            <FacebookProfileView
              selectedPage={selectedPage}
              isDropdownOpen={isDropdownOpen}
              dropdownRef={dropdownRef}
              buttonRef={buttonRef}
              pagesLoading={pagesLoading}
              displayPages={availablePages}
              onPageSelect={handlePageSelect}
              userInfoEmail={apiIntegration?.configuration?.userInfo?.email}
              userInfoName={apiIntegration?.configuration?.userInfo?.name}
            />
          ) : isTikTokConnected ? (
            <TikTokSettingsPanel
              displayName={tkDisplayName}
              username={tkUsername}
              avatarUrl={tkAvatarUrl}
              isVerified={tkIsVerified}
              initialValues={tikTokInitialValues}
              saving={updatingSettings}
              onSave={handleSaveTikTokSettings}
            />
          ) : isConnected && integrationType === 'email' && apiIntegration?.configuration?.fromName ? (
            <div className="max-w-6xl mx-auto grid grid-cols-2 gap-6">
              <EmailProfileView
                fromName={apiIntegration.configuration.fromName}
                fromEmail={apiIntegration.configuration.fromEmail}
              />

              <CustomerListPanel
                customers={customers}
                customersLoading={customersLoading}
                showAddCustomerForm={showAddCustomerForm}
                onShowAddCustomerForm={() => setShowAddCustomerForm(true)}
                onHideAddCustomerForm={() => setShowAddCustomerForm(false)}
                onSaveCustomer={handleSaveCustomer}
                onFileUpload={handleFileUpload}
                medium = "email"
              />
            </div>
          ) : isConnected && integrationType === 'sms' && apiIntegration?.configuration ? (
            <div className="max-w-6xl mx-auto flex flex-row gap-5">
              <SmsProfileView
                agentNumber={
                  apiIntegration.configuration.agentNumber
                }
              />

              <CustomerListPanel
                customers={customers}
                customersLoading={customersLoading}
                showAddCustomerForm={showAddCustomerForm}
                onShowAddCustomerForm={() =>
                  setShowAddCustomerForm(true)
                }
                onHideAddCustomerForm={() =>
                  setShowAddCustomerForm(false)
                }
                onSaveCustomer={handleSaveCustomer}
                onFileUpload={handleFileUpload}
                medium = "sms"
              />
            </div>
          ) : isConnected &&
            apiIntegration?.provider?.toLowerCase() === 'instagram_direct' && apiIntegration?.configuration?.userInfo?.username ? (
            <InstagramProfileView userInfo={apiIntegration.configuration.userInfo} />
          ) : isConnected && !hasPagesConfigured && (integrationType === 'facebook' || (integrationType === 'instagram' && apiIntegration?.provider?.toLowerCase() !== 'instagram_direct')) ? (
            <FacebookPageSelector
              integrationName={integration.name}
              pagesLoading={pagesLoading}
              facebookPages={facebookPages}
              selectedPageForUpdate={selectedPageForUpdate}
              onSelectPage={setSelectedPageForUpdate}
              onConfirmSelect={handleSelectPageClick}
            />
          ) : (
            <ConnectSection
              integrationName={integration.name}
              integrationType={integration.type}
              isActivating={isActivating}
              loading={loading}
              emailFromName={emailFromName}
              onEmailFromNameChange={setEmailFromName}
              onConnect={handleToggle}
              onInstagramFacebookConnect={handleInstagramFacebookConnect}
              onInstagramDirectConnect={handleInstagramDirectConnect}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default IntegrationDetail;
